import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { N8nInstance } from '@n8n-builder/api-client';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';
import { CACHE_TTL, fetchWithCache } from '../utils/cache';

type ConnectionStatus = 'idle' | 'loading' | 'testing' | 'connected' | 'error';

export type ChatMode = 'plan' | 'build' | 'edit';
export type GenerationModel = 'sonnet-4.5' | 'haiku-4.5' | 'opus-4.5';
export type SettingsTab = 'profile' | 'credits' | 'subscription' | 'n8n-connection' | 'danger-zone';
export type Theme = 'light' | 'dark';

interface SettingsState {
  activeInstance: N8nInstance | null;
  connectionStatus: ConnectionStatus;
  connectionError: string | null;
  chatMode: ChatMode;
  selectedModel: GenerationModel;
  theme: Theme;
  isSettingsModalOpen: boolean;
  activeSettingsTab: SettingsTab;
  isUpgradeModalOpen: boolean;
  activeInstanceLastFetchedAt: number | null;
  setActiveInstance: (instance: N8nInstance | null) => void;
  setConnectionStatus: (status: ConnectionStatus, error?: string | null) => void;
  hasValidCredentials: () => boolean;
  clearActiveInstance: () => void;
  setChatMode: (mode: ChatMode) => void;
  setSelectedModel: (model: GenerationModel) => void;
  setTheme: (theme: Theme) => void;
  openSettingsModal: (tab?: SettingsTab) => void;
  closeSettingsModal: () => void;
  setActiveSettingsTab: (tab: SettingsTab) => void;
  openUpgradeModal: () => void;
  closeUpgradeModal: () => void;
  fetchActiveInstance: (options?: { force?: boolean }) => void;
  invalidateActiveInstance: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      activeInstance: null,
      connectionStatus: 'idle',
      connectionError: null,
      chatMode: 'plan',
      selectedModel: 'sonnet-4.5',
      theme: 'light',
      isSettingsModalOpen: false,
      activeSettingsTab: 'profile',
      isUpgradeModalOpen: false,
      activeInstanceLastFetchedAt: null,
      setActiveInstance: (activeInstance) =>
        set({
          activeInstance,
          connectionStatus: activeInstance?.status === 'connected' ? 'connected' : 'idle',
          connectionError: null,
          activeInstanceLastFetchedAt: Date.now(),
        }),
      setConnectionStatus: (connectionStatus, connectionError = null) =>
        set({ connectionStatus, connectionError }),
      hasValidCredentials: () => {
        const state = get();
        return Boolean(state.activeInstance);
      },
      clearActiveInstance: () =>
        set({ activeInstance: null, connectionStatus: 'idle', connectionError: null, activeInstanceLastFetchedAt: null }),
      setChatMode: (chatMode) => set({ chatMode }),
      setSelectedModel: (selectedModel) => set({ selectedModel }),
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      openSettingsModal: (tab) =>
        set({ isSettingsModalOpen: true, activeSettingsTab: tab ?? get().activeSettingsTab }),
      closeSettingsModal: () => set({ isSettingsModalOpen: false }),
      setActiveSettingsTab: (activeSettingsTab) => set({ activeSettingsTab }),
      openUpgradeModal: () => set({ isUpgradeModalOpen: true }),
      closeUpgradeModal: () => set({ isUpgradeModalOpen: false }),
      fetchActiveInstance: (options) => {
        const state = get();
        fetchWithCache(
          state.activeInstance !== null,
          state.activeInstanceLastFetchedAt,
          CACHE_TTL.activeInstance,
          async () => {
            try {
              const apiClient = createApiClient(API_BASE_URL);
              const result = await apiClient.getActiveInstance();
              if (result.data) {
                set({
                  activeInstance: result.data.instance,
                  connectionStatus: result.data.instance?.status === 'connected' ? 'connected' : 'idle',
                  connectionError: null,
                  activeInstanceLastFetchedAt: Date.now(),
                });
              }
            } catch (error) {
              console.error('Failed to fetch active instance:', error);
            }
          },
          options,
        );
      },
      invalidateActiveInstance: () => set({ activeInstanceLastFetchedAt: null }),
    }),
    {
      name: 'n8n-builder-settings',
      partialize: (state) => ({
        // Don't persist 'edit' mode - it's context-dependent
        chatMode: state.chatMode === 'edit' ? 'plan' : state.chatMode,
        selectedModel: state.selectedModel,
        theme: state.theme,
      }),
    }
  )
);

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}
